import { useCallback, useMemo } from "react";

import {
  useApiMutation,
  useApiQuery,
  useQueryClient,
} from "~/hooks/useApiQuery";

export type LotName = {
  _id: string;
  lotReference: string;
  killDate: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

const QUERY_KEY = "/user/gmp/LoadLotNames";

export const useLotNames = () => {
  const queryClient = useQueryClient();

  const lotNamesQuery = useApiQuery<LotName[]>({
    path: QUERY_KEY,
    config: { method: "post", data: {} },
    shouldDecodeSuccess: true,
  });

  const lotNames = useMemo(
    () => lotNamesQuery.data ?? [],
    [lotNamesQuery.data],
  );

  const addNameMutation = useApiMutation<LotName[]>({
    path: "/user/gmp/AddLotName",
    config: { method: "post" },
    shouldDecodeSuccess: true,
    options: {
      onSuccess: (data) => {
        queryClient.setQueryData([QUERY_KEY, {}, undefined], data);
      },
    },
  });

  const updateNameMutation = useApiMutation<LotName[]>({
    path: "/user/gmp/UpdateLotName",
    config: { method: "post" },
    shouldDecodeSuccess: true,
    options: {
      onSuccess: (data) => {
        queryClient.setQueryData([QUERY_KEY, {}, undefined], data);
      },
    },
  });

  const deleteNameMutation = useApiMutation<LotName[]>({
    path: "/user/gmp/DeleteLotName",
    config: { method: "post" },
    shouldDecodeSuccess: true,
    options: {
      onSuccess: (data) => {
        queryClient.setQueryData([QUERY_KEY, {}, undefined], data);
      },
    },
  });

  const getNameForLot = useCallback(
    (lotReference: string, killDate: string): string | undefined => {
      return lotNames.find(
        (n) => n.lotReference === lotReference && n.killDate === killDate,
      )?.name;
    },
    [lotNames],
  );

  const getNameRecord = useCallback(
    (lotReference: string, killDate: string): LotName | undefined => {
      return lotNames.find(
        (n) => n.lotReference === lotReference && n.killDate === killDate,
      );
    },
    [lotNames],
  );

  const mutationLoading =
    addNameMutation.isPending ||
    updateNameMutation.isPending ||
    deleteNameMutation.isPending;

  return {
    lotNames,
    lotNamesQuery,
    getNameForLot,
    getNameRecord,
    addNameMutation,
    updateNameMutation,
    deleteNameMutation,
    mutationLoading,
  };
};
